import { useRef, useState } from 'react';
import { useEventListener, useLoad, useSubscription } from 'shared/hooks';
import { SESSION_CHANGED } from 'shared/api/session';
import { api, errorText, REPORT_ASSETS_CHANGED } from './api';
import { assetIds } from './assetReferences';
import { isHtmlLink, readLinkAsset } from 'features/html-reporting/public/linkReads';

/** Private bytes enter through the verified host, never an iframe SAS URL.
 * Data URLs exist only in the rendering copy: host Blob URLs cannot cross the
 * opaque iframe's storage partition. The model still receives only asset IDs.
 */
export class ReportAssetCache {
  private pending = new Map<string, Promise<string>>();
  private closed = false;
  /** An HTML page reads its images through its own route, which anyone who can open the page may use (page-only readers included).
   * The report's file library is for report editors and is never the display path: the page route serves only an image the page references. */
  constructor(private pageUid: string) {}
  load(id: string): Promise<string> {
    if (this.closed) return Promise.reject(new Error('The report session changed.'));
    let pending = this.pending.get(id);
    if (!pending) {
      pending = (isHtmlLink(this.pageUid) ? readLinkAsset(id) : api.bytes('/html/pages/' + this.pageUid + '/assets/' + id + '/content')).then(async blob => {
        if (this.closed) throw new Error('The report session changed.');
        const url = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error('The report image could not be read. Retry the page.'));
          reader.readAsDataURL(blob);
        });
        if (this.closed) throw new Error('The report session changed.');
        return url;
      }).catch(error => { if (this.pending.get(id) === pending) this.pending.delete(id); throw error; });
      this.pending.set(id, pending);
    }
    return pending;
  }
  invalidate(id: string) { this.pending.delete(id); }
  dispose() { this.closed = true; this.pending.clear(); }
}

type Resolution = { key: string; urls: Record<string, string>; errors: { message: string; phase?: string; source?: string }[] };
const empty = {urls: {} as Record<string,string>, errors: [] as { message: string; phase?: string; source?: string }[]};
export function useReportAssets(reportId: string, source: string, refreshKey: number, pageUid: string) {
  const [session, setSession] = useState(0);
  const [imports, setImports] = useState(0);
  const cache = useRef<ReportAssetCache | null>(null);
  const idsKey = JSON.stringify(assetIds(source));
  const key = JSON.stringify([reportId, pageUid, idsKey, session, refreshKey, imports]);
  const [result, setResult] = useState<Resolution>({key: '', urls: {}, errors: []});
  const imported = (event: Event) => {
    const detail = (event as CustomEvent).detail;
    if (detail?.reportId?.toLowerCase() !== reportId.toLowerCase()) return;
    const missing: string[] = JSON.parse(idsKey).filter((id:string)=>(!detail.assetId || detail.assetId===id) && (result.key!==key || !result.urls[id]));
    if (!missing.length) return;
    missing.forEach(id=>cache.current?.invalidate(id)); setImports(value=>value+1);
  };
  useEventListener(REPORT_ASSETS_CHANGED, imported);
  // One cache per report and session; a new session (sign-in change) starts a new one.
  useSubscription(() => {
    const current = new ReportAssetCache(pageUid); cache.current = current;
    return () => { current.dispose(); cache.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, pageUid, session]);
  const changed = () => { cache.current?.dispose(); setSession(s => s + 1); };
  useEventListener(SESSION_CHANGED, changed);
  useLoad((signal) => {
    const ids: string[] = JSON.parse(idsKey);
    if (ids.length) void Promise.all(ids.map(async id => {
      try { return {id, url: await cache.current!.load(id)}; }
      catch (error) { return {id, error: errorText(error)}; }
    })).then(entries => {
      if (signal.aborted) return;
      setResult({key, urls: Object.fromEntries(entries.filter(e => e.url).map(e => [e.id, e.url!])),
        errors: entries.filter(e => e.error).map(e => ({phase: 'resource', source: 'unity-asset:' + e.id,
          message: 'Report image could not load: ' + e.error}))});
    });
  }, [idsKey, key]);
  return idsKey === '[]' ? {ready: true, ...empty} :
    result.key === key ? {ready: true, urls: result.urls, errors: result.errors} : {ready: false, ...empty};
}
